/**
 * A fila de espera, dentro da tela de Pedidos.
 *
 * Mora aqui e não numa aba própria porque Pedidos já é a tela das coisas que
 * aguardam o barbeiro — e um quinto item na barra de baixo, em 390px, espreme
 * os alvos de toque abaixo dos 44px que o polegar dele precisa.
 *
 * A parte que faz a funcionalidade valer é a de cima: **quem cabe hoje**. Ela
 * recarrega sozinha quando o SSE avisa que alguém cancelou, então o barbeiro
 * larga a tela aberta no balcão e o nome aparece.
 */

import type { ServiceView, WaitlistEntryView, WaitlistMatchView } from '@barbearia/shared';
import { describeServices, shortServicesLabel } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Avatar } from '../../components/PhotoPicker.tsx';
import { Badge, Button, ErrorNote, Field, OptionList, Sheet } from '../../components/ui.tsx';
import { useWaitlistWindow } from '../../components/WaitlistWindow.tsx';
import { ApiError, api, subscribeToAgenda } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { duration, relativeDay, timeOf, todayIn } from '../../lib/format.ts';

export function WaitlistSection() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const today = todayIn(tenant.timezone);

  // Sete dias, não só hoje: quem cancela na quarta o horário da sexta abre a
  // vaga que mais interessa, porque é a que ainda dá tempo de preencher.
  const { data: matchData } = useQuery({
    queryKey: ['waitlist-matches', today],
    queryFn: () =>
      api.get<{ matches: WaitlistMatchView[] }>('/waitlist/matches', { date: today, days: 7 }),
  });

  const { data: listData } = useQuery({
    queryKey: ['waitlist'],
    queryFn: () => api.get<{ entries: WaitlistEntryView[] }>('/waitlist'),
  });

  // Cancelamento é justamente o que abre vaga. Sem isto, o barbeiro precisaria
  // recarregar a tela para descobrir que agora tem gente que cabe.
  useEffect(() => {
    return subscribeToAgenda(() => {
      queryClient.invalidateQueries({ queryKey: ['waitlist-matches'] });
    });
  }, [queryClient]);

  const matches = matchData?.matches ?? [];
  const entries = listData?.entries ?? [];

  if (entries.length === 0) return null;

  const matchedIds = new Set(matches.map((match) => match.entry.id));
  const waiting = entries.filter((entry) => !matchedIds.has(entry.id));

  return (
    <>
      {matches.length > 0 && (
        <section className="px-4 pt-6">
          <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Tem vaga para
          </h2>
          <ul className="flex flex-col gap-3">
            {matches.map((match) => (
              <MatchCard key={match.entry.id} match={match} />
            ))}
          </ul>
        </section>
      )}

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          {matches.length > 0 ? 'Sem vaga por enquanto' : 'Aguardando vaga'}
        </h2>

        {waiting.length === 0 ? (
          <p className="px-1 text-[13px] text-ink-faint">
            Todo mundo da fila tem alguma vaga nos próximos dias.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-line">
            {waiting.map((entry) => (
              <WaitingRow key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/**
 * Alguém que cabe numa vaga.
 *
 * O cartão mostra os horários que servem para **aquela pessoa**, já filtrados
 * pela duração dela. Escolher um horário abre as duas coisas que o barbeiro faz
 * em seguida, na ordem em que ele faz: avisar e, quando o cliente responder,
 * marcar. Nenhuma das duas tira ele desta tela.
 */
function MatchCard({ match }: { match: WaitlistMatchView }) {
  const { tenant } = useTenant();
  const today = todayIn(tenant.timezone);
  const queryClient = useQueryClient();

  const [chosen, setChosen] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { entry } = match;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    await queryClient.invalidateQueries({ queryKey: ['waitlist-matches'] });
    await queryClient.invalidateQueries({ queryKey: ['agenda'] });
  };

  const notify = useMutation({
    mutationFn: (startsAt: string) =>
      api.get<{ whatsapp: { url: string } }>(`/waitlist/${entry.id}/whatsapp`, { startsAt }),
    onSuccess: (result) => {
      window.open(result.whatsapp.url, '_blank');
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  const book = useMutation({
    mutationFn: (input: { startsAt: string; acceptConflict: boolean }) =>
      api.post(`/waitlist/${entry.id}/book`, input),
    onSuccess: async () => {
      setConflict(null);
      setChosen(null);
      await refresh();
    },
    onError: (caught) => {
      // O horário que ele ofereceu pode ter sido tomado no meio do caminho. A
      // API devolve o conflito em vez de marcar por cima calado, e a tela
      // pergunta — porque encaixar é decisão dele, não do sistema.
      if (caught instanceof ApiError && caught.status === 409) {
        setConflict(caught.message);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Não deu certo');
    },
  });

  const busy = notify.isPending || book.isPending;

  return (
    <li className="overflow-hidden rounded-2xl border border-primary/40 bg-primary/5">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar name={entry.client.name} photoUrl={entry.client.photoUrl} size={44} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-semibold text-ink">{entry.client.name}</p>
          <p className="text-[13px] text-ink-soft">
            {describeServices(entry.services.map((service) => service.name))} ·{' '}
            {duration(entry.durationMinutes)}
          </p>
          <p className="text-[13px] text-ink-faint">pediu: {entry.summary}</p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          {entry.client.totalNoShows > 0 && (
            <Badge tone="danger">
              {entry.client.totalNoShows} {entry.client.totalNoShows === 1 ? 'falta' : 'faltas'}
            </Badge>
          )}
          {entry.notifyCount > 0 && <Badge>já avisado</Badge>}
        </div>
      </div>

      {entry.note && <p className="px-4 pb-2 text-[13px] text-ink-soft italic">“{entry.note}”</p>}

      <div className="flex flex-col gap-2 border-t border-primary/30 p-3">
        <p className="text-[13px] text-ink-soft">
          {chosen ? 'Horário escolhido:' : 'Qual horário serve para ele?'}
        </p>

        <div className="flex flex-wrap gap-2">
          {match.starts.slice(0, 8).map((start) => (
            <button
              key={start}
              type="button"
              onClick={() => {
                setChosen(start === chosen ? null : start);
                setConflict(null);
              }}
              className={`flex touch-target flex-col items-center rounded-lg border px-3 leading-tight ${
                start === chosen
                  ? 'border-primary bg-primary text-primary-ink'
                  : 'border-line bg-surface'
              }`}
            >
              <span
                className={`text-[11px] ${start === chosen ? 'text-primary-ink/80' : 'text-ink-faint'}`}
              >
                {relativeDay(start.slice(0, 10), today, tenant.timezone)}
              </span>
              <span
                className={`text-[15px] font-semibold tabular-nums ${
                  start === chosen ? 'text-primary-ink' : 'text-ink'
                }`}
              >
                {timeOf(start, tenant.timezone)}
              </span>
            </button>
          ))}
        </div>

        {chosen && (
          <div className="flex flex-col gap-2 pt-1">
            {conflict ? (
              <>
                <ErrorNote>{conflict}</ErrorNote>
                <Button
                  variant="danger"
                  full
                  loading={book.isPending}
                  onClick={() => book.mutate({ startsAt: chosen, acceptConflict: true })}
                >
                  Encaixar mesmo assim
                </Button>
              </>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  loading={notify.isPending}
                  onClick={() => notify.mutate(chosen)}
                >
                  Avisar
                </Button>
                <Button
                  className="flex-1"
                  loading={book.isPending}
                  onClick={() => book.mutate({ startsAt: chosen, acceptConflict: false })}
                >
                  Marcar
                </Button>
              </div>
            )}

            {error && <ErrorNote>{error}</ErrorNote>}

            <p className="text-[12px] text-ink-faint">
              "Avisar" abre o WhatsApp e não reserva nada. "Marcar" põe na agenda e tira da fila.
            </p>
          </div>
        )}

        {!chosen && busy && <p className="text-[12px] text-ink-faint">Um instante…</p>}
      </div>
    </li>
  );
}

/** Quem está na fila mas não cabe em nada hoje. Linha discreta, sem ação. */
function WaitingRow({ entry }: { entry: WaitlistEntryView }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/waitlist/${entry.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      await queryClient.invalidateQueries({ queryKey: ['waitlist-matches'] });
      setOpen(false);
    },
  });

  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full touch-target items-center gap-3 border-b border-line bg-surface-raised px-3 py-2.5 text-left last:border-b-0"
        >
          <Avatar name={entry.client.name} photoUrl={entry.client.photoUrl} size={36} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] text-ink">{entry.client.name}</span>
            <span className="block truncate text-[12px] text-ink-faint">
              {shortServicesLabel(entry.services.map((service) => service.name))} · {entry.summary}
            </span>
          </span>
          <span className="shrink-0 text-[18px] text-ink-faint">›</span>
        </button>
      </li>

      {open && (
        <Sheet
          open
          onClose={() => setOpen(false)}
          title={entry.client.name}
          footer={
            <Button
              variant="danger"
              full
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Tirar da lista de espera
            </Button>
          }
        >
          <dl className="overflow-hidden rounded-xl border border-line">
            <Row label="Quer" value={entry.summary} />
            <Row
              label={entry.services.length > 1 ? 'Serviços' : 'Serviço'}
              value={`${describeServices(entry.services.map((service) => service.name))} · ${duration(entry.durationMinutes)}`}
            />
            <Row label="Com" value={entry.staff?.name ?? 'Qualquer profissional'} />
            <Row label="Vale até" value={entry.untilDate.split('-').reverse().join('/')} />
            {entry.notifyCount > 0 && (
              <Row
                label="Já avisado"
                value={`${entry.notifyCount} ${entry.notifyCount === 1 ? 'vez' : 'vezes'}`}
              />
            )}
          </dl>

          {entry.note && (
            <div className="mt-4 rounded-xl border border-line bg-surface-raised px-4 py-3">
              <p className="pb-1 text-[12px] font-semibold text-ink-faint">Recado</p>
              <p className="text-[14px] text-ink">{entry.note}</p>
            </div>
          )}

          <p className="pt-4 text-[13px] text-ink-faint">
            Não há horário livre nos próximos dias que caiba nessa faixa. Aparece aqui em cima assim
            que alguém cancelar.
          </p>
        </Sheet>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-2.5 last:border-b-0">
      <dt className="text-[13px] text-ink-faint">{label}</dt>
      <dd className="text-right text-[14px] font-medium text-ink">{value}</dd>
    </div>
  );
}

/**
 * "Deixa na espera aí."
 *
 * Abre da ficha do cliente, que é onde o barbeiro está quando ouve a frase. Os
 * atalhos de faixa cobrem quase todo pedido real em um toque — quem quiser
 * precisão abre "outra faixa" e escolhe dia e hora.
 *
 * O profissional não é perguntado: a espera nasce valendo para qualquer um, que
 * é o que "me avisa se vagar" quer dizer. O contrato aceita escolher, para
 * quando a barbearia tiver equipe grande o bastante para isso importar.
 */
export function NewWaitlistSheet({
  clientId,
  clientName,
  onClose,
}: {
  clientId: string;
  clientName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
  });

  const services = (data?.services ?? []).filter((service) => service.active);

  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { value: window, picker } = useWaitlistWindow();

  const create = useMutation({
    mutationFn: () =>
      api.post('/waitlist', {
        clientId,
        serviceIds,
        ...window,
        note: note.trim() || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      await queryClient.invalidateQueries({ queryKey: ['waitlist-matches'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title="Aguardar vaga"
      footer={
        <Button
          full
          disabled={serviceIds.length === 0}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          {serviceIds.length > 0 ? 'Colocar na espera' : 'Escolha o serviço'}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-[14px] text-ink-soft">
          {clientName} vai ser avisado quando abrir um horário que sirva.
        </p>

        <section>
          <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Serviços
          </h3>
          <OptionList
            items={services}
            selected={serviceIds}
            onSelect={(service) =>
              setServiceIds((current) =>
                current.includes(service.id)
                  ? current.filter((id) => id !== service.id)
                  : [...current, service.id],
              )
            }
            getKey={(service) => service.id}
            renderTitle={(service) => service.name}
            renderMeta={(service) => duration(service.defaultDurationMinutes)}
          />
        </section>

        <section>
          <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Quando serve
          </h3>
          {picker}
        </section>

        <Field
          label="Recado (opcional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Só depois do trabalho"
        />

        {error && <ErrorNote>{error}</ErrorNote>}

        <p className="text-[13px] text-ink-faint">
          Vale por 30 dias. Marcar um horário não tira da lista — dá para ter o horário que sobrou e
          continuar esperando um melhor.
        </p>
      </div>
    </Sheet>
  );
}
